<template>
  <template v-if="$route.name === 'groups.index'">
    <GroupFilters 
      @search="handleSearch"
      @add="openAddGroupDialog"
    />
    
    <GroupTable
      :class-groups="filteredGroups"
      :class-name="t('groups.allGroups')"
      :class-id="null"
      :loading="isLoading"
      @delete-group="handleDeleteGroup"
    />
    
    <PaginationControls
      v-if="pagination.totalItems > 0 && !isLoading"
      :pagination="{
        currentPage: pagination.currentPage,
        perPage: pagination.perPage,
        totalItems: pagination.total,
        totalPages: pagination.lastPage,
        from: pagination.from,
        to: pagination.to
      }"
      @page-changed="handlePageChange"
    />
  </template>
  
  <router-view></router-view>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import GroupFilters from '@/components/features/groups/filters/GroupFilter.vue'
import GroupTable from '@/components/features/groups/table/GroupTable.vue'
import PaginationControls from '@/components/common/PaginationControls.vue'
import { useStore } from 'vuex'
import { debounce } from '../../utils/debounce'

const { t } = useI18n()
const store = useStore()
const toast = useToast()
const isLoading = ref(false)

// State
const groups = computed(() => store.state.groups.groups || [])
const pagination = computed(() => store.state.groups.pagination || {
  currentPage: 1,
  perPage: 10,
  total: 0,
  lastPage: 1
})
const searchTerm = ref('')
let searchDebounceToken = null

// Computed
const filteredGroups = computed(() => {
  if (!searchTerm.value) return groups.value
  return groups.value.filter(group => 
    group?.name?.toLowerCase().includes(searchTerm.value.toLowerCase())
  )
})

// Methods
const fetchGroups = async (page = 1, perPage = 10) => {
  isLoading.value = true
  try {
    const params = { 
      page: Number(page),
      per_page: Number(perPage)
    }
    
    if (searchTerm.value) {
      params.search = searchTerm.value
    }
    
    await store.dispatch('groups/fetchAllGroups', params)
  } catch (error) {
    if (!error.isCancel) {
      console.error("API error:", error)
      if (groups.value.length === 0) {
        toast.error(error.response?.data?.message || t('groups.failedLoadGroups'))
      }
    }
  } finally {
    isLoading.value = false
  }
}

const handlePageChange = (newPage) => {
  fetchGroups(newPage, pagination.value.perPage)
}

const handleSearch = (term) => {
  if (searchDebounceToken) {
    searchDebounceToken.cancel()
  }
  
  searchTerm.value = term
  searchDebounceToken = debounce(async (term, cancelToken) => {
    if (!cancelToken.isCancel) {
      await fetchGroups(1, pagination.value.perPage)
    }
  }, 300)
  
  searchDebounceToken(term)
}

const handleDeleteGroup = async (groupId) => {
  isLoading.value = true
  try {
    await store.dispatch('groups/deleteGroup', groupId)
    toast.success(t('groups.groupDeleted'))
    
    const currentGroupsCount = groups.value.length
    const currentPage = pagination.value.currentPage
    if (currentGroupsCount === 1 && currentPage > 1) {
      await fetchGroups(currentPage - 1, pagination.value.perPage)
    } else {
      await fetchGroups(currentPage, pagination.value.perPage)
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error ||
                        error.message || 
                        t('groups.failedDelete')
    
    if (errorMessage.includes('foreign key constraint')) {
      toast.error(t('groups.deleteLinked'))
    } else {
      toast.error(errorMessage)
    }
  } finally {
    isLoading.value = false
  }
}

// Initial load
onMounted(() => {
  fetchGroups()
})

// Cleanup
onUnmounted(() => {
  if (searchDebounceToken) {
    searchDebounceToken.cancel()
  }
})
</script>
